import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { initialize, updateSyncErrors } from 'redux-form';
import { connect } from 'react-redux';
import classNames from 'classnames';

import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import Button from '@material-ui/core/Button';

import {
  AppHeader,
  BusinessOwnerForm,
  CircularLoader,
  Pagination
} from 'components';
import { EditIcon, TrashIcon } from 'components/icons';
import { EXISTING_BUSINESS_OWNERS } from 'graphql/queries/business-owner';
import ConfirmContext from 'contexts/ConfirmContext';
import { setCurrentBusinessOwner } from 'actions/businessOwner';
import {
  CREATE_BUSINESS_OWNER,
  DELETE_BUSINESS_OWNER,
  UPDATE_BUSINESS_OWNER_USER
} from 'graphql/mutations/business-owner';

import useStyles from './styles';

const headingsConfig = classes => {
  return [
    {
      label: 'Name',
      align: 'left',
      cellClassName: classes.nameCell
    },
    {
      label: 'Email',
      cellClassName: classes.emailCell,
      align: 'left',
    },
    {
      label: 'Phone',
      cellClassName: classes.unitCell,
      align: 'left'
    },
    {
      label: 'Company name',
      align: 'left',
      cellClassName: classes.guestsCell
    },
    {
      label: 'Reference prefix',
      cellClassName: classes.checkInCell,
      align: 'left'
    },
    {
      label: 'Email notification',
      cellClassName: classes.checkInCell,
      align: 'left'
    },
    { label: 'Actions', align: 'right' }
  ];
};

const BusinessOwners = ({
  initializeForm,
  updateFormErrors,
  setBusinessOwner
}) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const confirm = React.useContext(ConfirmContext);
  const history = useHistory();

  const { enqueueSnackbar } = useSnackbar();

  const [createBusinessOwner] = useMutation(CREATE_BUSINESS_OWNER);
  const [deleteBusinessOwner] = useMutation(DELETE_BUSINESS_OWNER);
  const [updateBusinessOwner] = useMutation(UPDATE_BUSINESS_OWNER_USER);

  const [page, setPage] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const [isUpdate, setIsUpdate] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { data, refetch } = useQuery(EXISTING_BUSINESS_OWNERS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      page: page
    }
  });

  const openInfoDialog = businessOwner => {
    setOpen(true);
    initializeForms({
      businessOwnerForm: {
        ...businessOwner,
        selectBusinessOwner: {
          'Select Business Owner': false
        },
        isNotify: {
          Notify: businessOwner?.isNotify || false
        }
      }
    });
  };

  const openCreateBusinessOwnerDialog = () => {
    initializeForms({
      businessOwnerForm: {
        selectBusinessOwner: {
          'Select Business Owner': false
        },
        isNotify: {
          Notify: false
        }
      }
    });
    setIsUpdate(false);
    setOpen(true);
  };

  const onCloseHandler = () => {
    setOpen(false);
  };

  const selectBusinessOwner = data => {
    localStorage.setItem('selectedBusinessOwner', JSON.stringify(data));
    setBusinessOwner(data);
    setTimeout(() => history.go(), 3500);
  };

  const onSubmitHandler = values => {
    const data = { ...values };
    data.isNotify = data.isNotify.Notify;
    delete data.selectBusinessOwner;
    delete data.referencePrefix;

    if (!isUpdate) {
      createBusinessOwner({
        variables: { businessOwner: data }
      })
        .then(data => {
          setLoading(true);
          enqueueSnackbar(t('Business owner was successfully created!'), {
            variant: 'success'
          });
          refetch();
          setOpen(false);
          selectBusinessOwner({
            ...values,
            id: data.data.createBusinessOwner.id
          });
        })
        .catch(error => {
          if (
            error.message ===
            'GraphQL error: User with this email is already in use'
          ) {
            updateFormErrors({ email: 'This email is already in use' });
            return;
          }

          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
        });
    } else {
      delete data.__typename;
      updateBusinessOwner({
        variables: { businessOwner: data }
      }).then(() => {
        setLoading(true);
        selectBusinessOwner(values);
        enqueueSnackbar(t('Business owner was successfully updated!'), {
          variant: 'success'
        });
        refetch();
        setOpen(false);
      });
    }
  };

  const initializeForms = initialFormsData => {
    Object.keys(initialFormsData).map(field => {
      initializeForm(field, initialFormsData[field]);
    });
  };

  const deleteBusinessOwnerHandler = item => {
    confirm({
      title: `Delete Business owner?`,
      description: `${item.firstName} ${item.lastName}`,
      confirmationText: 'Delete',
      cancellationText: 'Back'
    }).then(() =>
      deleteBusinessOwner({ variables: { businessOwnerId: item.id } }).then(
        () => {
          enqueueSnackbar(t('Business owner was successfully deleted!'), {
            variant: 'success'
          });
          if (data.businessOwners.length <= 1 && page) {
            refetch({
              page: page - 1
            });
            setPage(page - 1);
          } else {
            refetch({ page });
          }
        }
      )
    );
  };

  const onPageChangeHandler = (e, page) => {
    refetch({
      page: page - 1
    });
    setPage(page - 1);
  };

  return (
    <div className={classes.root}>
      <CircularLoader
        isLoading={loading}
        loaderClassName={classes.loaderClassName}
      />
      <AppHeader />
      <div className={classes.toolbar}>
        <div className={classes.toolbarLeft}>
          <Typography variant="h2" className={classes.toolbarTitle}>
            {t('Business owners')}
          </Typography>
        </div>
        <div className={classes.toolbarRight}>
          <Button
            variant="outlined"
            color="primary"
            classes={{ root: classes.buttonRoot }}
            onClick={e => {
              e.stopPropagation();
              openCreateBusinessOwnerDialog();
            }}
          >
            {t('Add Business Owner')}
          </Button>
        </div>
      </div>
      <div className={classes.tabsRoot}>
        <TableContainer component="div" classes={{ root: classes.container }}>
          <Table className={classes.table} aria-label="simple table">
            <TableHead className={classes.tableHead}>
              <TableRow>
                {headingsConfig(classes).map(heading => (
                  <TableCell
                    key={heading.label}
                    align={heading.align}
                    style={{ paddingBottom: 15 }}
                    className={classNames(
                      classes.tableCell,
                      classes.heading,
                      heading.cellClassName
                    )}
                  >
                    {t(heading.label)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data &&
                data.businessOwners &&
                data.businessOwners.map(businessOwner => (
                  <TableRow
                    key={businessOwner.id}
                    classes={{ root: classes.tableRow }}
                  >
                    <TableCell
                      style={{ paddingBottom: 5, paddingTop: 5 }}
                      className={classNames(
                        classes.tableCell,
                        classes.nameCell
                      )}
                      align="left"
                    >
                      {businessOwner.firstName} {businessOwner.lastName}
                    </TableCell>
                    <TableCell
                      style={{ paddingBottom: 5, paddingTop: 5 }}
                      className={classNames(
                        classes.tableCell,
                        classes.emailCell,
                      )}
                      align="left"
                    >
                      {businessOwner.email}
                    </TableCell>
                    <TableCell
                      style={{ paddingBottom: 5, paddingTop: 5 }}
                      className={classNames(
                        classes.tableCell,
                        classes.phoneCell
                      )}
                      align="left"
                    >
                      {businessOwner.phoneNumber}
                    </TableCell>
                    <TableCell
                      style={{ paddingBottom: 5, paddingTop: 5 }}
                      className={classNames(
                        classes.tableCell,
                        classes.companyCell
                      )}
                      align="left"
                    >
                      {businessOwner.companyName}
                    </TableCell>
                    <TableCell
                      style={{ paddingBottom: 5, paddingTop: 5 }}
                      className={classNames(
                        classes.tableCell,
                        classes.referenceCell
                      )}
                      align="left"
                    >
                      {businessOwner.referencePrefix}
                    </TableCell>
                    <TableCell
                      style={{ paddingBottom: 5, paddingTop: 5 }}
                      className={classNames(
                        classes.tableCell,
                        classes.companyCell
                      )}
                      align="left"
                    >
                      {businessOwner.isNotify ? 'ON' : 'OFF'}
                    </TableCell>
                    <TableCell
                      style={{ paddingBottom: 5, paddingTop: 5 }}
                      className={classes.tableCell}
                      align="right"
                    >
                      <div
                        style={{ display: 'inline-flex', paddingRight: '10px' }}
                      >
                        <IconButton
                          className={classes.iconButton}
                          disableTouchRipple={true}
                          disableFocusRipple={true}
                          aria-label="edit"
                          onClick={e => {
                            e.stopPropagation();
                            setIsUpdate(true);
                            openInfoDialog(businessOwner);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          disableTouchRipple={true}
                          disableFocusRipple={true}
                          className={classes.iconButton}
                          aria-label="delete"
                          onClick={e => {
                            e.stopPropagation();
                            deleteBusinessOwnerHandler(businessOwner);
                          }}
                        >
                          <TrashIcon />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              {open && (
                <BusinessOwnerForm
                  isUpdate={isUpdate}
                  onClose={onCloseHandler}
                  open={open}
                  onSubmit={values => {
                    onSubmitHandler(values);
                  }}
                />
              )}
            </TableBody>
          </Table>

          {data && (
            <Pagination
              page={page + 1}
              count={data.numberOfOwnersPages}
              onChange={onPageChangeHandler}
              amount={data.businessOwners.length}
              total={data.businessOwnersOverview.total}
            />
          )}
        </TableContainer>
      </div>
    </div>
  );
};
const mapStateToProps = state => ({
  businessOwnerForm: state.form.businessOwnerForm
});

const mapDispatchToProps = dispatch => ({
  initializeForm: (form, data) => {
    dispatch(initialize(form, data));
  },
  updateFormErrors: fields => {
    dispatch(updateSyncErrors('businessOwnerForm', fields));
  },
  setBusinessOwner: item => dispatch(setCurrentBusinessOwner(item))
});

export default connect(mapStateToProps, mapDispatchToProps)(BusinessOwners);
